// src/pages/Group.jsx
import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import "../global.css";
import "../styles/faqPage.css";
import ReviewsBar from "../components/ReviewsBar";

const Group = () => {
  return (
    <div className="faq-page">
      {/* ==============================================
          🔍 SEO META & SCHEMA
      =============================================== */}
      <Helmet>
        <title>
          About CheapCabGrab Group – Taxis, Minibus Hire, HomeFoodGrab & IT Services
        </title>
        <meta
          name="description"
          content="Learn about CheapCabGrab Group – a UK-registered company providing licensed taxi & minibus transfers, the HomeFoodGrab local food platform, and CCG Technologies for AI and IT consulting."
        />
        <link rel="canonical" href="https://www.cheapcabgrab.com/group" />
        <meta
          name="keywords"
          content="CheapCabGrab Group, taxi company, minibus hire, HomeFoodGrab, IT consultancy, AI services, CCG Technologies"
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content="CheapCabGrab Group – Transport, Food & Technology"
        />
        <meta
          property="og:description"
          content="CheapCabGrab Group operates licensed taxi & minibus services, the HomeFoodGrab food marketplace, and CCG Technologies for IT and AI solutions."
        />
        <meta property="og:url" content="https://www.cheapcabgrab.com/group" />

        {/* JSON-LD Org Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "CheapCabGrab Group Ltd",
            url: "https://www.cheapcabgrab.com",
            description:
              "CheapCabGrab Group provides airport & city taxi services, minibus and coach hire, the HomeFoodGrab local food marketplace, and CCG Technologies for IT, cybersecurity and AI solutions.",
            sameAs: [
              "https://www.cheapcabgrab.com/heathrow-taxi",
              "https://www.cheapcabgrab.com/minibus-hire",
              "https://www.homefoodgrab.com",
            ],
            brand: [
              {
                "@type": "Brand",
                name: "CheapCabGrab",
                url: "https://www.cheapcabgrab.com",
              },
              {
                "@type": "Brand",
                name: "HomeFoodGrab",
                url: "https://www.homefoodgrab.com",
              },
              {
                "@type": "Brand",
                name: "CCG Technologies",
                url: "https://www.cheapcabgrab.com/group",
              },
            ],
          })}
        </script>
      </Helmet>

      {/* ==============================================
          MAIN LAYOUT (matches FAQ / Terms / Privacy)
      =============================================== */}
      <div className="faq-grid">
        {/* LEFT: MAIN CONTENT */}
        <div className="faq-left">
          <header className="faq-header">
            <h1>About CheapCabGrab Group</h1>
            <p>
              CheapCabGrab Group is a UK-registered company operating across{" "}
              <strong>Transport</strong>, <strong>Food</strong> and{" "}
              <strong>Technology</strong>.  
              Our goal is to make everyday journeys, meals and digital services
              more reliable, transparent and affordable.
            </p>
          </header>

          {/* 1. Who we are */}
          <section className="faq-section">
            <h2>1. Who We Are</h2>
            <p>
              CheapCabGrab Group Ltd is a multi-division organisation based in the UK,
              combining:
            </p>
            <ul>
              <li>🚖 <strong>CheapCabGrab</strong> – licensed taxi, airport and minibus services</li>
              <li>🍲 <strong>HomeFoodGrab</strong> – local food marketplace for verified home cooks</li>
              <li>💻 <strong>CCG Technologies</strong> – IT, AI and security consultancy</li>
            </ul>
            <p>
              We operate with a strong focus on{" "}
              <strong>compliance</strong>, <strong>safety</strong> and{" "}
              <strong>customer trust</strong>, aligning with UK private hire and
              data protection regulations.
            </p>
          </section>

          {/* 2. CheapCabGrab – taxis & minibuses */}
          <section className="faq-section">
            <h2>2. CheapCabGrab – Licensed Taxi & Minibus Services</h2>
            <p>
              <strong>CheapCabGrab</strong> provides fixed-price{" "}
              <strong>airport and city transfers</strong> with fully licensed,
              council-approved private hire drivers.
            </p>
            <ul>
              <li>✈️ Heathrow, Gatwick, Luton, Stansted & London hotel transfers</li>
              <li>🚕 Local cabs for areas like Slough, Windsor, Maidenhead and nearby</li>
              <li>🚐 Minibus hire for 7–14 passengers and group trips</li>
              <li>🚌 Access to partner coach services for 20+ seat groups</li>
              <li>💳 Secure online booking with Stripe payments</li>
            </ul>
            <p>
              Start with an instant quote on our{" "}
              <Link className="faq-link" to="/">
                main booking page
              </Link>{" "}
              or see:
            </p>
            <ul>
              <li>
                <Link className="faq-link" to="/heathrow-taxi">
                  Heathrow taxi page
                </Link>
              </li>
              <li>
                <Link className="faq-link" to="/minibus-hire">
                  Minibus hire (7–14 seater)
                </Link>
              </li>
              <li>
                <Link className="faq-link" to="/coach">
                  Coach hire overview
                </Link>
              </li>
              <li>
                <Link className="faq-link" to="/our-drivers">
                  Our drivers & licensing
                </Link>
              </li>
            </ul>
          </section>

          {/* 3. HomeFoodGrab */}
          <section className="faq-section">
            <h2>3. HomeFoodGrab – Local Food, Made Fresh</h2>
            <p>
              <strong>HomeFoodGrab</strong> is our community-focused food platform,
              enabling verified home cooks to sell freshly made meals to nearby
              customers while working with local councils on hygiene and compliance.
            </p>
            <ul>
              <li>🥘 Support for genuine, home-cooked meals</li>
              <li>📍 Local, short-distance deliveries to reduce waste</li>
              <li>✅ Council registration & hygiene awareness for sellers</li>
              <li>🚗 Integration with our delivery & driver network over time</li>
            </ul>
            <p>
              For more information, visit{" "}
              <a
                className="faq-link"
                href="https://www.homefoodgrab.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                www.homefoodgrab.com
              </a>{" "}
              or contact us via the{" "}
              <Link className="faq-link" to="/contact">
                contact page
              </Link>
              .
            </p>
          </section>

          {/* 4. CCG Technologies */}
          <section className="faq-section">
            <h2>4. CCG Technologies – IT, AI & Cybersecurity</h2>
            <p>
              <strong>CCG Technologies</strong> is the technology arm of CheapCabGrab Group,
              helping organisations modernise and automate their operations.
            </p>
            <ul>
              <li>🤖 AI integration and intelligent agents for operations</li>
              <li>🔐 Cybersecurity reviews and best-practice hardening</li>
              <li>🌐 Full-stack web / API development and optimisation</li>
              <li>📊 Data pipelines and reporting to support decision-making</li>
            </ul>
            <p>
              To discuss a project, email{" "}
              <a className="faq-link" href="mailto:info@cheapcabgrab.com">
                info@cheapcabgrab.com
              </a>{" "}
              and our technology team will be in touch.
            </p>
          </section>

          {/* 5. Values & compliance */}
          <section className="faq-section">
            <h2>5. Our Values & Compliance</h2>
            <ul>
              <li>✅ Safety first – for passengers, drivers and food customers</li>
              <li>✅ Clear pricing – no hidden extras or surge surprises</li>
              <li>✅ Respect & inclusion – diversity and accessibility focused</li>
              <li>✅ Data protection – GDPR-aligned policies and controls</li>
            </ul>
            <p>
              You can read more in our{" "}
              <Link className="faq-link" to="/terms">
                Terms & Conditions
              </Link>{" "}
              and{" "}
              <Link className="faq-link" to="/privacy">
                Privacy Policy
              </Link>
              .
            </p>
          </section>

          {/* 6. Company summary */}
          <section className="faq-section">
            <h2>6. Company Summary</h2>
            <p>
              <strong>CheapCabGrab Group Ltd</strong> – UK Registered Company
            </p>
            <ul>
              <li>SIC 49320 – Taxi Operation</li>
              <li>SIC 56290 – Other Food Service Activities</li>
              <li>SIC 62020 – Information Technology Consultancy Activities</li>
            </ul>
            <p>
              For general enquiries, please use our{" "}
              <Link className="faq-link" to="/contact">
                contact form
              </Link>{" "}
              or email{" "}
              <a className="faq-link" href="mailto:info@cheapcabgrab.com">
                info@cheapcabgrab.com
              </a>
              .
            </p>
          </section>
        </div>

        {/* RIGHT: SIDEBAR */}
        <aside className="faq-right">
          <div className="faq-reviews-card">
            <h2>⭐ Trusted, Local & Tech-Driven</h2>
            <p>
              One group, three connected services – taxis, food and technology –
              all focused on reliability and long-term trust.
            </p>
            <div style={{ marginTop: "10px" }}>
              <ReviewsBar />
            </div>
          </div>

          <div className="faq-cta-card">
            <h3>Quick Taxi Links</h3>
            <ul>
              <li>
                <Link className="faq-link" to="/">
                  🚖 Instant Taxi Quote
                </Link>
              </li>
              <li>
                <Link className="faq-link" to="/heathrow-taxi">
                  ✈️ Heathrow Taxi Page
                </Link>
              </li>
              <li>
                <Link className="faq-link" to="/minibus-hire">
                  🚐 Minibus Hire
                </Link>
              </li>
              <li>
                <Link className="faq-link" to="/our-drivers">
                  👨‍✈️ Our Drivers & Standards
                </Link>
              </li>
            </ul>
          </div>

          <div className="faq-cta-card">
            <h3>Need to Talk?</h3>
            <p>
              For B2B partnerships, hotel contracts or IT projects,
              reach out through our{" "}
              <Link className="faq-link" to="/contact">
                contact page
              </Link>
              .
            </p>
          </div>
        </aside>
      </div>

      {/* Floating CTA */}
      <button
        className="floating-book-btn"
        onClick={() => (window.location.href = "/")}
      >
        🚖 Book Now
      </button>
    </div>
  );
};

export default Group;

